import type { ReactNode } from "react";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import {
  EmptyMachine,
  MachineBackdrop,
  type MachineArt,
} from "@volca/ui/components/ui/empty-machine";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";

interface MachineEmptyStateProps {
  art: MachineArt;
  title: ReactNode;
  description?: ReactNode;
  footnote?: ReactNode;
  layout?: "panel" | "workspace";
  children?: ReactNode;
  active?: boolean;
  className?: string;
}

export const MachineEmptyState = ({
  art,
  title,
  description,
  footnote,
  layout = "panel",
  children,
  active,
  className,
}: MachineEmptyStateProps) => (
  <Empty
    data-machine-host
    className={cn(
      emptyStateVariants({ layout }),
      "@container/machine-empty relative isolate justify-center-safe overflow-clip",
      className,
    )}
  >
    <MachineBackdrop
      floor={art.floor}
      className="hidden @2xs/machine-empty:block"
    />
    <Stack
      gap={6}
      align="center"
      className={cn(
        "w-full @2xl/machine-empty:flex-row @2xl/machine-empty:gap-10",
        {
          "max-w-176": layout === "panel",
          "max-w-4xl @2xl/machine-empty:justify-center @2xl/machine-empty:gap-12":
            layout === "workspace",
        },
      )}
    >
      {/* Beside the text, the art gets one width whatever its shape, so the
          text starts at the same place on every empty state. */}
      <EmptyMedia
        className={cn("m-0 hidden min-w-0 @2xs/machine-empty:flex", {
          "@2xl/machine-empty:order-2 @2xl/machine-empty:w-80":
            layout === "workspace",
        })}
      >
        <EmptyMachine
          art={art}
          active={active}
          className={cn("max-w-full", {
            "h-48 @md/machine-empty:h-56 @2xl/machine-empty:h-64":
              layout === "panel",
            "h-56 @md/machine-empty:h-64 @2xl/machine-empty:h-80":
              layout === "workspace",
          })}
        />
      </EmptyMedia>
      <Stack
        gap={5}
        align="center"
        className={cn(
          "w-full text-center @2xl/machine-empty:items-start @2xl/machine-empty:text-start",
          {
            "max-w-88": layout === "panel",
            "max-w-96": layout === "workspace",
          },
        )}
      >
        <EmptyHeader className="w-full max-w-none gap-3 @2xl/machine-empty:items-start">
          <EmptyTitle
            className={cn("leading-snug", {
              "text-base": layout === "panel",
              "text-2xl font-semibold": layout === "workspace",
            })}
          >
            {title}
          </EmptyTitle>
          {description && (
            // Balanced lines leave a short paragraph narrower than its
            // column; a paragraph wraps to the column and keeps its last
            // word company.
            <EmptyDescription className="text-pretty">
              {description}
            </EmptyDescription>
          )}
        </EmptyHeader>
        {children && (
          <EmptyContent
            data-machine-trigger
            className="@2xl/machine-empty:items-start"
          >
            {children}
          </EmptyContent>
        )}
        {footnote && (
          <Text
            tone="muted"
            className={cn("max-w-88 text-pretty", {
              "mt-3": layout === "panel",
              "mt-6 @2xl/machine-empty:mt-10": layout === "workspace",
            })}
          >
            {footnote}
          </Text>
        )}
      </Stack>
    </Stack>
  </Empty>
);
